import { Head, Link, usePage } from "@inertiajs/react"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import DownloadIcon from "@mui/icons-material/Download"
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Container,
    Divider,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Typography,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers"
import dayjs, { Dayjs } from "dayjs"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

// ─── Types ────────────────────────────────────────────────────────────────────

interface IClientReport {
    client_name: string
    orders_count: number
    reference_paper_color_display: string
    programmed_kilograms: number
    total_kilograms: number
}

interface IProductReport {
    reference_name: string
    orders_count: number
    reference_paper_color_display: string
    programmed_kilograms: number
    total_kilograms: number
}

interface IMonthlyOrder {
    id: number
    order_code: string
    client_name: string
    project_name: string
    reference_name: string
    reference_paper_color: number
}

type Props = {
    back_params: Record<string, string | null>
}

const PAPER_COLOR: Record<number, string> = { 0: "N/A", 1: "White", 2: "Kraft" }

const fmt = (n: number | string) =>
    Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// ─── Date filter bar ──────────────────────────────────────────────────────────

function DateRangeBar({
    startDate, endDate, loading,
    onStartChange, onEndChange,
}: {
    startDate: Dayjs; endDate: Dayjs; loading: boolean
    onStartChange: (d: Dayjs) => void; onEndChange: (d: Dayjs) => void
}) {
    const { t } = useTranslation()
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
            <DatePicker
                label={t("common.startDate")}
                value={startDate}
                onChange={d => d && onStartChange(d)}
                format="DD-MM-YYYY"
                slotProps={{ textField: { size: "small" } }}
            />
            <DatePicker
                label={t("common.endDate")}
                value={endDate}
                onChange={d => d && onEndChange(d)}
                format="DD-MM-YYYY"
                slotProps={{ textField: { size: "small" } }}
            />
            {loading && <CircularProgress size={20} />}
        </Box>
    )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function OrdersReports() {
    const { back_params } = usePage<Props>().props
    const { t } = useTranslation()
    const cleanBack = Object.fromEntries(Object.entries(back_params).filter(([, v]) => v != null)) as Record<string, string>
    const backUrl = `/orders?${new URLSearchParams(cleanBack).toString()}`

    const [tab, setTab] = useState(0)
    const [loading, setLoading] = useState(false)
    const [loadError, setLoadError] = useState(false)
    const [downloadError, setDownloadError] = useState(false)

    const [startDate, setStartDate] = useState<Dayjs>(dayjs().startOf("month"))
    const [endDate, setEndDate] = useState<Dayjs>(dayjs().endOf("month"))
    const [monthDate, setMonthDate] = useState<Dayjs>(dayjs())
    const [pendingMonth, setPendingMonth] = useState<Dayjs>(dayjs())

    const [clients, setClients] = useState<IClientReport[]>([])
    const [products, setProducts] = useState<IProductReport[]>([])
    const [monthlyOrders, setMonthlyOrders] = useState<IMonthlyOrder[]>([])
    const [selected, setSelected] = useState<number[]>([])

    // Aborting on cleanup keeps a slow earlier response from overwriting the
    // rows of the filters currently on screen.
    useEffect(() => {
        const controller = new AbortController()
        const range = `start_date=${startDate.format("YYYY-MM-DD")}&end_date=${endDate.format("YYYY-MM-DD")}`
        const url = tab === 0
            ? `/orders/orders_per_client?${range}`
            : tab === 1
            ? `/orders/orders_per_product?${range}`
            : `/orders/orders_by_month?month=${monthDate.month() + 1}&year=${monthDate.year()}`

        setLoading(true)
        setLoadError(false)
        fetch(url, { signal: controller.signal })
            .then(res => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`)
                return res.json()
            })
            .then(data => {
                if (tab === 0) setClients(data)
                else if (tab === 1) setProducts(data)
                else { setMonthlyOrders(data); setSelected([]) }
            })
            .catch(err => {
                if ((err as Error).name === "AbortError") return
                setLoadError(true)
                if (tab === 0) setClients([])
                else if (tab === 1) setProducts([])
                else { setMonthlyOrders([]); setSelected([]) }
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false)
            })

        return () => controller.abort()
    }, [tab, startDate, endDate, monthDate])

    // ── Per-client/product totals ─────────────────────────────────────────────

    const clientTotals = clients.reduce(
        (acc, r) => ({ orders: acc.orders + Number(r.orders_count), prog: acc.prog + Number(r.programmed_kilograms), total: acc.total + Number(r.total_kilograms) }),
        { orders: 0, prog: 0, total: 0 }
    )
    const productTotals = products.reduce(
        (acc, r) => ({ orders: acc.orders + Number(r.orders_count), prog: acc.prog + Number(r.programmed_kilograms), total: acc.total + Number(r.total_kilograms) }),
        { orders: 0, prog: 0, total: 0 }
    )

    // ── Monthly selection ─────────────────────────────────────────────────────

    const allSelected = selected.length === monthlyOrders.length && monthlyOrders.length > 0
    const someSelected = selected.length > 0 && selected.length < monthlyOrders.length

    const toggleAll = () => setSelected(allSelected ? [] : monthlyOrders.map(o => o.id))
    const toggleOne = (id: number) =>
        setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])

    // ── Excel downloads ───────────────────────────────────────────────────────

    const downloadClientExcel = () => {
        window.location.href =
            `/orders/orders_per_client_excel?start_date=${startDate.format("YYYY-MM-DD")}&end_date=${endDate.format("YYYY-MM-DD")}`
    }

    const downloadProductExcel = () => {
        window.location.href =
            `/orders/orders_per_product_excel?start_date=${startDate.format("YYYY-MM-DD")}&end_date=${endDate.format("YYYY-MM-DD")}`
    }

    const downloadGroupExcel = async () => {
        const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? ""
        setDownloadError(false)
        try {
            const res = await fetch("/orders/generate_orders_group_excel", {
                method: "POST",
                headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf },
                body: JSON.stringify({ orders_ids: selected, date: monthDate.format("YYYY-MM-DD") }),
            })
            if (!res.ok) {
                setDownloadError(true)
                return
            }
            const blob = await res.blob()
            const url = URL.createObjectURL(blob)
            const a = document.createElement("a")
            a.href = url
            a.download = `orders-${monthDate.format("MMMM-YYYY")}.xlsx`
            a.click()
            URL.revokeObjectURL(url)
        } catch {
            setDownloadError(true)
        }
    }

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Head title={`${t("orders.title")} | ${t("orders.reports.title")}`} />

            <Box sx={{ mb: 2 }}>
                <Button LinkComponent={Link} href={backUrl} size="small" startIcon={<ArrowBackIcon />} color="inherit">
                    {t("orders.title")}
                </Button>
            </Box>

            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 3 }}>{t("orders.reports.title")}</Typography>

            {loadError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLoadError(false)}>
                    {t("common.loadError")}
                </Alert>
            )}

            {downloadError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDownloadError(false)}>
                    {t("common.downloadError")}
                </Alert>
            )}

            <Paper variant="outlined">
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}>
                    <Tab label={t("orders.reports.perClient")} />
                    <Tab label={t("orders.reports.perProduct")} />
                    <Tab label={t("orders.reports.monthly")} />
                </Tabs>

                <Box sx={{ p: 2 }}>
                    {/* ── Per Client ── */}
                    {tab === 0 && (
                        <>
                            <DateRangeBar startDate={startDate} endDate={endDate} loading={loading} onStartChange={setStartDate} onEndChange={setEndDate} />
                            <Divider sx={{ mb: 2 }} />
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: "action.hover" }}>
                                            <TableCell colSpan={4}><strong>{t("orders.reports.clientHeader")}</strong></TableCell>
                                            <TableCell align="right">
                                                <Button size="small" variant="contained" color="success" startIcon={<DownloadIcon />}
                                                    onClick={downloadClientExcel} disabled={clients.length === 0}>
                                                    {t("common.excel")}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow sx={{ bgcolor: "action.hover" }}>
                                            <TableCell>{t("common.name")}</TableCell>
                                            <TableCell align="right">{t("orders.reports.ordersCount")}</TableCell>
                                            <TableCell>{t("orders.reports.paperColor")}</TableCell>
                                            <TableCell align="right">{t("orders.reports.progKgs")}</TableCell>
                                            <TableCell align="right">{t("orders.reports.totalKgs")}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {clients.map((r, i) => (
                                            <TableRow key={i} hover>
                                                <TableCell>{r.client_name}</TableCell>
                                                <TableCell align="right">{r.orders_count}</TableCell>
                                                <TableCell>{r.reference_paper_color_display}</TableCell>
                                                <TableCell align="right">{fmt(r.programmed_kilograms)}</TableCell>
                                                <TableCell align="right">{fmt(r.total_kilograms)}</TableCell>
                                            </TableRow>
                                        ))}
                                        {clients.length === 0 && !loading && (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                                                    {t("common.noDataRange")}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {clients.length > 0 && (
                                            <TableRow sx={{ bgcolor: "action.hover", fontWeight: "bold" }}>
                                                <TableCell><strong>{t("common.total")}</strong></TableCell>
                                                <TableCell align="right"><strong>{clientTotals.orders}</strong></TableCell>
                                                <TableCell />
                                                <TableCell align="right"><strong>{fmt(clientTotals.prog)}</strong></TableCell>
                                                <TableCell align="right"><strong>{fmt(clientTotals.total)}</strong></TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}

                    {/* ── Per Product ── */}
                    {tab === 1 && (
                        <>
                            <DateRangeBar startDate={startDate} endDate={endDate} loading={loading} onStartChange={setStartDate} onEndChange={setEndDate} />
                            <Divider sx={{ mb: 2 }} />
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: "action.hover" }}>
                                            <TableCell colSpan={4}><strong>{t("orders.reports.productHeader")}</strong></TableCell>
                                            <TableCell align="right">
                                                <Button size="small" variant="contained" color="success" startIcon={<DownloadIcon />}
                                                    onClick={downloadProductExcel} disabled={products.length === 0}>
                                                    {t("common.excel")}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow sx={{ bgcolor: "action.hover" }}>
                                            <TableCell>{t("common.name")}</TableCell>
                                            <TableCell align="right">{t("orders.reports.ordersCount")}</TableCell>
                                            <TableCell>{t("orders.reports.paperColor")}</TableCell>
                                            <TableCell align="right">{t("orders.reports.progKgs")}</TableCell>
                                            <TableCell align="right">{t("orders.reports.totalKgs")}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {products.map((r, i) => (
                                            <TableRow key={i} hover>
                                                <TableCell>{r.reference_name}</TableCell>
                                                <TableCell align="right">{r.orders_count}</TableCell>
                                                <TableCell>{r.reference_paper_color_display}</TableCell>
                                                <TableCell align="right">{fmt(r.programmed_kilograms)}</TableCell>
                                                <TableCell align="right">{fmt(r.total_kilograms)}</TableCell>
                                            </TableRow>
                                        ))}
                                        {products.length === 0 && !loading && (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                                                    {t("common.noDataRange")}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {products.length > 0 && (
                                            <TableRow sx={{ bgcolor: "action.hover" }}>
                                                <TableCell><strong>{t("common.total")}</strong></TableCell>
                                                <TableCell align="right"><strong>{productTotals.orders}</strong></TableCell>
                                                <TableCell />
                                                <TableCell align="right"><strong>{fmt(productTotals.prog)}</strong></TableCell>
                                                <TableCell align="right"><strong>{fmt(productTotals.total)}</strong></TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}

                    {/* ── Monthly ── */}
                    {tab === 2 && (
                        <>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
                                <DatePicker
                                    label={t("orders.reports.monthAndYear")}
                                    value={pendingMonth}
                                    onChange={d => d && setPendingMonth(d)}
                                    onAccept={d => d && setMonthDate(d)}
                                    views={["month", "year"]}
                                    format="MM-YYYY"
                                    slotProps={{ textField: { size: "small" } }}
                                />
                                {loading && <CircularProgress size={20} />}
                                {selected.length > 0 && (
                                    <Button variant="contained" color="success" startIcon={<DownloadIcon />} onClick={downloadGroupExcel}>
                                        {t("common.export", { count: selected.length, total: monthlyOrders.length })}
                                    </Button>
                                )}
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: "action.hover" }}>
                                            <TableCell padding="checkbox">
                                                <Checkbox size="small" checked={allSelected} indeterminate={someSelected}
                                                    onChange={toggleAll} disabled={monthlyOrders.length === 0} />
                                            </TableCell>
                                            <TableCell>{t("orders.reports.code")}</TableCell>
                                            <TableCell>{t("orders.reports.client")}</TableCell>
                                            <TableCell>{t("orders.reports.project")}</TableCell>
                                            <TableCell>{t("orders.reports.product")}</TableCell>
                                            <TableCell>{t("orders.reports.paperColor")}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {monthlyOrders.map(o => (
                                            <TableRow key={o.id} hover selected={selected.includes(o.id)}>
                                                <TableCell padding="checkbox">
                                                    <Checkbox size="small" checked={selected.includes(o.id)} onChange={() => toggleOne(o.id)} />
                                                </TableCell>
                                                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{o.order_code}</TableCell>
                                                <TableCell>{o.client_name}</TableCell>
                                                <TableCell>{o.project_name}</TableCell>
                                                <TableCell>{o.reference_name || "—"}</TableCell>
                                                <TableCell>{PAPER_COLOR[o.reference_paper_color] ?? "—"}</TableCell>
                                            </TableRow>
                                        ))}
                                        {monthlyOrders.length === 0 && !loading && (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                                                    {t("orders.reports.noOrders")}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </Box>
            </Paper>
        </Container>
    )
}
